import { useEffect, useState } from "react";
import { Mail, Sparkles, Send, Forward, RefreshCw } from "lucide-react";

const SAMPLES = [
    {
        from: "e.schneider@musterweg-12.de",
        subject: "Heizung in Wohnung 4 defekt",
        body: "Guten Tag, seit gestern funktioniert die Heizung in meiner Wohnung nicht mehr. Können Sie sich darum kümmern? Mit freundlichen Grüßen, Eva Schneider",
        intent: "Störungsmeldung",
        auto: true,
        action: "Automatisch beantwortet",
        reply: "Sehr geehrte Frau Schneider, Ihre Meldung ist erfasst. Der Hausmeister wurde informiert, der Techniker-Termin wird Ihnen innerhalb von 24 Stunden bestätigt. Bei Rückfragen melden Sie sich gerne jederzeit.",
    },
    {
        from: "t.krause@krause-bau.de",
        subject: "Angebot für Badsanierung",
        body: "Hallo, wir möchten unser Bad sanieren lassen, ca. 6 m². Was würde das ungefähr kosten und wann hätten Sie Kapazität? Gruß, Thomas Krause",
        intent: "Angebotsanfrage",
        auto: true,
        action: "Automatisch beantwortet",
        reply: "Guten Tag Herr Krause, gerne erstellen wir Ihnen ein Angebot. Für eine erste Einschätzung benötigen wir noch zwei Fotos des Bads und Ihre Wunschzeitspanne. Unsere nächsten freien Kapazitäten liegen ab März.",
    },
    {
        from: "s.vogel@vogel-consulting.de",
        subject: "Individuelles Projekt – Beratung nötig",
        body: "Sehr geehrte Damen und Herren, wir planen ein größeres Projekt mit mehreren Gewerken und brauchen dazu eine persönliche Beratung. Können wir einen Termin vereinbaren? Viele Grüße, Sabine Vogel",
        intent: "Beratungsbedarf",
        auto: false,
        action: "Weitergeleitet an die zuständige Person",
        reply: "Kein Standardfall – wird mit Zusammenfassung und Dringlichkeit „hoch” an die zuständige Person weitergeleitet, inkl. Terminvorschlag für ein Erstgespräch. Die Kundin erhält automatisch eine Eingangsbestätigung.",
    },
];

export const Simulator = () => {
    const [index, setIndex] = useState(0);
    const [phase, setPhase] = useState("analyzing");
    const sample = SAMPLES[index];

    useEffect(() => {
        setPhase("analyzing");
        const t = setTimeout(() => setPhase("done"), 1400);
        return () => clearTimeout(t);
    }, [index]);

    return (
        <section className="py-20 lg:py-28 border-t border-white/[0.06]" aria-labelledby="simulator-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-emerald-400">Live-Vorschau</p>
                <h2 id="simulator-heading" className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl">
                    So sieht das im Alltag aus.
                </h2>
                <p className="mt-4 text-base sm:text-lg text-[#9CA3AF] leading-relaxed max-w-2xl">
                    Eine Beispielanfrage rein, Entscheidung raus. Klicken Sie sich durch drei typische Fälle aus
                    verschiedenen Branchen.
                </p>
                <div data-testid="ai-simulator" className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                    <div className="rounded-2xl border border-white/[0.08] bg-[#161F30] p-6 sm:p-8">
                        <div className="flex items-center gap-2 text-xs font-mono-label uppercase tracking-[0.2em] text-[#6B7280]">
                            <Mail className="h-4 w-4" aria-hidden="true" />
                            Eingehende Anfrage
                        </div>
                        <div className="mt-5 rounded-xl border border-white/[0.08] bg-[#0B0F17]/70 p-5">
                            <p className="text-xs text-[#6B7280]">Von: {sample.from}</p>
                            <p data-testid="simulator-subject" className="mt-2 text-base font-medium text-white">{sample.subject}</p>
                            <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{sample.body}</p>
                        </div>
                        <button
                            type="button"
                            data-testid="simulator-next-button"
                            onClick={() => setIndex((index + 1) % SAMPLES.length)}
                            className="mt-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 px-5 py-2.5 text-sm font-medium text-emerald-400 transition-colors duration-200 hover:bg-emerald-500/10"
                        >
                            <RefreshCw className="h-4 w-4" aria-hidden="true" />
                            Nächste Beispielanfrage
                        </button>
                    </div>
                    <div className="rounded-2xl border border-emerald-500/20 bg-[#161F30] p-6 sm:p-8">
                        <div className="flex items-center gap-2 text-xs font-mono-label uppercase tracking-[0.2em] text-emerald-400">
                            <Sparkles className="h-4 w-4" aria-hidden="true" />
                            Kirenzo-Entscheidung
                        </div>
                        {phase === "analyzing" ? (
                            <div data-testid="simulator-loading" className="mt-5 rounded-xl border border-white/[0.08] bg-[#0B0F17]/70 p-5">
                                <div className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                                    <span className="status-dot h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
                                    Anfrage wird analysiert …
                                </div>
                                <div className="mt-4 space-y-2">
                                    <div className="h-3 w-2/3 rounded bg-white/[0.06] animate-pulse" />
                                    <div className="h-3 w-1/2 rounded bg-white/[0.06] animate-pulse" />
                                    <div className="h-3 w-3/5 rounded bg-white/[0.06] animate-pulse" />
                                </div>
                            </div>
                        ) : (
                            <div data-testid="simulator-result" className="fade-up mt-5 rounded-xl border border-white/[0.08] bg-[#0B0F17]/70 p-5">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span data-testid="simulator-intent" className="rounded-full bg-emerald-500/15 border border-emerald-500/30 px-3 py-1 text-xs font-medium text-emerald-300">
                                        Erkannt: {sample.intent}
                                    </span>
                                    <span data-testid="simulator-action" className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] border border-white/[0.08] px-3 py-1 text-xs text-[#9CA3AF]">
                                        {sample.auto ? <Send className="h-3 w-3" aria-hidden="true" /> : <Forward className="h-3 w-3" aria-hidden="true" />}
                                        {sample.action}
                                    </span>
                                </div>
                                <p className="mt-4 text-sm text-[#9CA3AF] leading-relaxed">{sample.reply}</p>
                            </div>
                        )}
                        <p className="mt-6 text-xs text-[#6B7280] leading-relaxed">
                            Vereinfachte Darstellung. In der Demo zeigen wir Ihnen den echten Ablauf mit den
                            Prozessen und Antwortvorlagen Ihres Unternehmens.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};
